/**
 * Modal de revisión admin: muestra los valores de la entrega y archivos,
 * permite aprobar/rechazar con notas.
 */

import { useState } from "react";
import { Modal, ModalFooter, Button } from "@/components/ui";
import {
  SUBMISSION_FIELD_TYPE_LABEL,
  SUBMISSION_STATUS_LABEL,
  getDisplaySubmissionStatus,
  toJsDate,
} from "@/lib/services/activitySubmissions";
import type {
  ClubActivitySubmission,
  EventActivity,
  SubmissionFileRef,
} from "@/types";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  submission: ClubActivitySubmission;
  activity: EventActivity | null;
  onReview: (decision: "approved" | "rejected", notes?: string) => Promise<void>;
}

export function SubmissionReviewModal({
  isOpen,
  onClose,
  submission,
  activity,
  onReview,
}: Props) {
  const [notes, setNotes] = useState(submission.reviewNotes ?? "");
  const [working, setWorking] = useState(false);
  const fields = (activity?.submissionRequirements?.fields ?? []).slice().sort(
    (a, b) => a.order - b.order,
  );

  const submit = async (decision: "approved" | "rejected") => {
    setWorking(true);
    try {
      await onReview(decision, notes.trim() || undefined);
    } finally {
      setWorking(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Revisión: ${submission.activityName}`}
      description={`${submission.clubName} · Estado actual: ${
        SUBMISSION_STATUS_LABEL[getDisplaySubmissionStatus(submission)]
      }${submission.isLate ? " · ⚠ tarde" : ""}`}
      size="lg"
    >
      <div className="space-y-4">
        <div className="text-xs text-gray-500 grid grid-cols-2 gap-2 p-2 bg-gray-50 rounded">
          {submission.submittedAt && (
            <div>
              <strong>Enviado:</strong>{" "}
              {toJsDate(submission.submittedAt)?.toLocaleString("es-CO")}
              {submission.submittedByName && ` por ${submission.submittedByName}`}
            </div>
          )}
          {submission.lastEditedAt && (
            <div>
              <strong>Última edición:</strong>{" "}
              {toJsDate(submission.lastEditedAt)?.toLocaleString("es-CO")}
            </div>
          )}
          {typeof submission.timingBandIndex === "number" && (
            <div>
              <strong>Timing band:</strong> #{submission.timingBandIndex}
            </div>
          )}
        </div>

        <div className="space-y-3">
          {fields.length === 0 && (
            <p className="text-sm text-gray-500 italic">
              No se conoce la configuración de campos de esta actividad.
            </p>
          )}
          {fields.map((f) => {
            const v = submission.values[f.id];
            return (
              <div key={f.id} className="border-b border-gray-100 pb-2">
                <div className="text-xs font-semibold text-gray-600">
                  {f.label}{" "}
                  <span className="font-normal text-gray-400">
                    ({SUBMISSION_FIELD_TYPE_LABEL[f.type]})
                  </span>
                </div>
                <div className="text-sm text-gray-900 mt-1">
                  <ValueRenderer value={v} type={f.type} />
                </div>
              </div>
            );
          })}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notas de revisión (opcional)
          </label>
          <textarea
            className="w-full p-2 border border-gray-300 rounded-lg text-sm"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Comentarios para el director del club…"
          />
        </div>
      </div>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose} disabled={working}>
          Cerrar
        </Button>
        <Button
          variant="danger"
          onClick={() => submit("rejected")}
          loading={working}
        >
          Rechazar
        </Button>
        <Button onClick={() => submit("approved")} loading={working}>
          Aprobar
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function ValueRenderer({
  value,
  type,
}: {
  value: unknown;
  type: string;
}) {
  if (value === null || value === undefined || value === "") {
    return <span className="text-gray-400 italic">—</span>;
  }
  if (type === "boolean") {
    return <span>{value ? "Sí" : "No"}</span>;
  }
  if (type === "url" && typeof value === "string") {
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline break-all"
      >
        {value}
      </a>
    );
  }
  if (type === "file" && value && typeof value === "object" && "url" in value) {
    const f = value as SubmissionFileRef;
    return (
      <a
        href={f.url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:underline"
      >
        📎 {f.name}
      </a>
    );
  }
  if (type === "multi_file" && Array.isArray(value)) {
    return (
      <ul className="list-disc list-inside space-y-0.5">
        {(value as SubmissionFileRef[]).map((f) => (
          <li key={f.path}>
            <a
              href={f.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              📎 {f.name}
            </a>
          </li>
        ))}
      </ul>
    );
  }
  if (Array.isArray(value)) {
    return <span>{value.join(", ")}</span>;
  }
  return <span className="whitespace-pre-wrap">{String(value)}</span>;
}
